import { useState, useCallback } from 'react';
import { apiClient } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { CHUNK_SIZE } from '../config';

interface UploadProgress {
  uploadId: string;
  fileName: string;
  fileSize: number;
  progress: number;
  uploadedChunks: number[];
  totalChunks: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
}

export function useChunkedUpload() {
  const { token } = useAuth();
  const [uploads, setUploads] = useState<Map<string, UploadProgress>>(new Map());

  const updateUploadProgress = useCallback((uploadId: string, updates: Partial<UploadProgress>) => {
    setUploads(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(uploadId) || {
        uploadId,
        fileName: '',
        fileSize: 0,
        progress: 0,
        uploadedChunks: [],
        totalChunks: 0,
        status: 'pending' as const,
      };
      newMap.set(uploadId, { ...current, ...updates });
      return newMap;
    });
  }, []);

  const removeUpload = useCallback((uploadId: string) => {
    setUploads(prev => {
      const newMap = new Map(prev);
      newMap.delete(uploadId);
      return newMap;
    });
  }, []);

  const uploadFile = useCallback(async (file: File): Promise<void> => {
    // 生成客户端上传ID作为临时标识
    const clientId = `upload_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    let serverUploadId: string | null = null;
    
    try {
      // 初始化上传
      const initResult = await apiClient.initUpload(token, {
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
      });

      serverUploadId = initResult.uploadId;
      const { totalChunks } = initResult;
      
      // 更新 uploadId 为服务器返回的 ID
      updateUploadProgress(serverUploadId, {
        uploadId: serverUploadId,
        fileName: file.name,
        fileSize: file.size,
        totalChunks,
        status: 'uploading',
      });

      // 读取文件并分片
      const arrayBuffer = await file.arrayBuffer();
      const fileData = new Uint8Array(arrayBuffer);

      // 上传分片 - 使用串行上传避免 CORS 问题
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, fileData.length);
        const chunkData = fileData.slice(start, end);

        // 转换为 base64 - 使用二进制字符串方法
        let binary = '';
        const bytes = new Uint8Array(chunkData);
        const len = bytes.byteLength;

        // 分块处理，避免栈溢出
        for (let i = 0; i < len; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        const base64Chunk = btoa(binary);

        try {
          const result = await apiClient.uploadChunk(token, {
            uploadId: serverUploadId!,
            chunkIndex: i,
            chunkData: base64Chunk,
          });

          // 更新进度
          updateUploadProgress(serverUploadId!, {
            progress: result.progress,
            uploadedChunks: result.uploadedChunks,
          });

          console.log(`分片 ${i + 1}/${totalChunks} 上传完成`);
        } catch (error) {
          console.error(`上传分片 ${i} 失败:`, error);
          const errorMessage = error instanceof Error
            ? `${error.message} (分片: ${i + 1}/${totalChunks})`
            : `上传分片 ${i + 1} 失败`;

          updateUploadProgress(serverUploadId!, {
            status: 'error',
            error: errorMessage,
          });
          throw error;
        }
      }

      // 完成上传
      updateUploadProgress(serverUploadId, { status: 'completed' });
      await apiClient.completeUpload(token, { uploadId: serverUploadId });
      
      toast.success(`${file.name} 上传完成`);
      
      // 清理上传记录
      setTimeout(() => {
        if (serverUploadId) {
          removeUpload(serverUploadId);
        }
      }, 3000);
      
    } catch (error) {
      console.error('上传文件失败:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : '未知错误';
      toast.error(`上传失败: ${errorMessage}`);
      
      // 尝试找到正确的 uploadId 来更新状态
      const actualUploadId = serverUploadId || clientId;
      
      updateUploadProgress(actualUploadId, {
        status: 'error',
        error: errorMessage,
      });
      throw error;
    }
  }, [token, updateUploadProgress, removeUpload]);

  const resumeUpload = useCallback(async (uploadId: string) => {
    try {
      const status = await apiClient.getUploadStatus(token, uploadId);
      
      // 更新进度
      updateUploadProgress(uploadId, {
        fileName: status.fileName,
        fileSize: status.fileSize,
        progress: status.progress,
        uploadedChunks: status.uploadedChunks,
        totalChunks: status.totalChunks,
        status: 'uploading',
        error: undefined
      });
      
      toast.info('正在恢复上传...');
    } catch (error) {
      console.error('恢复上传失败:', error);
      const errorMessage = error instanceof Error ? error.message : '恢复上传失败';
      toast.error(`恢复上传失败: ${errorMessage}`);
      
      updateUploadProgress(uploadId, {
        status: 'error',
        error: errorMessage,
      });
    }
  }, [token, updateUploadProgress]);

  const cancelUpload = useCallback((uploadId: string) => {
    updateUploadProgress(uploadId, { 
      status: 'error', 
      error: '用户取消上传' 
    });
    
    // 3秒后移除上传记录
    setTimeout(() => {
      removeUpload(uploadId);
    }, 3000);
    
    toast.info('上传已取消');
  }, [updateUploadProgress, removeUpload]);

  return {
    uploads: Array.from(uploads.values()),
    uploadFile,
    resumeUpload,
    cancelUpload,
    removeUpload,
    getUploadProgress: (uploadId: string) => uploads.get(uploadId),
  };
}